import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { firewallService, FirewallRisikoPort } from '../services/firewallService';
import { useNotification } from '../hooks/NotificationContext';
import ConfirmDialog from '../components/common/ConfirmDialog';

interface FormState {
  protokoll: 'tcp' | 'udp' | 'any';
  port_von: string;
  port_bis: string;
  grund: string;
  aktiv: boolean;
}

const emptyForm: FormState = { protokoll: 'tcp', port_von: '', port_bis: '', grund: '', aktiv: true };

const PROTOKOLL_LABEL: Record<string, string> = { tcp: 'TCP', udp: 'UDP', any: 'TCP/UDP' };

export const portText = (row: Pick<FirewallRisikoPort, 'port_von' | 'port_bis'>): string => (
  row.port_bis && row.port_bis !== row.port_von ? `${row.port_von}-${row.port_bis}` : String(row.port_von)
);

const parsePort = (value: string): number | null => {
  const n = Number(value);
  if (!value.trim() || !Number.isInteger(n) || n < 0 || n > 65535) return null;
  return n;
};

const FirewallRisikoportsPanel: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [rows, setRows] = useState<FirewallRisikoPort[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FirewallRisikoPort | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await firewallService.listRisikoports());
    } catch {
      showError('Risikoports konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row: FirewallRisikoPort) => {
    setEditing(row);
    setForm({
      protokoll: row.protokoll === 'udp' || row.protokoll === 'any' ? row.protokoll : 'tcp',
      port_von: String(row.port_von),
      port_bis: row.port_bis && row.port_bis !== row.port_von ? String(row.port_bis) : '',
      grund: row.grund,
      aktiv: row.aktiv,
    });
    setDialogOpen(true);
  };

  const portVon = parsePort(form.port_von);
  const portBis = form.port_bis.trim() ? parsePort(form.port_bis) : portVon;
  const formValid = portVon != null && portBis != null && portBis >= portVon && form.grund.trim().length > 0;

  const save = async () => {
    if (!formValid || portVon == null || portBis == null) return;
    const payload = {
      protokoll: form.protokoll,
      port_von: portVon,
      port_bis: portBis,
      grund: form.grund.trim(),
      aktiv: form.aktiv,
    };
    try {
      if (editing) {
        await firewallService.updateRisikoport(editing.id, payload);
      } else {
        await firewallService.createRisikoport(payload);
      }
      setDialogOpen(false);
      showSuccess('Risikoport gespeichert');
      await load();
    } catch {
      showError('Risikoport konnte nicht gespeichert werden.');
    }
  };

  const remove = async () => {
    if (deleteId == null) return;
    try {
      await firewallService.deleteRisikoport(deleteId);
      showSuccess('Risikoport gelöscht');
      setDeleteId(null);
      await load();
    } catch {
      showError('Risikoport konnte nicht gelöscht werden.');
    }
  };

  return (
    <Box data-testid="firewall-risikoports-panel">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Ports, die in Firewall-Regeln als Risiko markiert werden (z. B. RDP, SMB, Telnet).
          Der Abgleich erfolgt beim Lesen der Regeln, bestehende Bewertungen bleiben unverändert.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={{ flexShrink: 0 }}>
          Risikoport
        </Button>
      </Box>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Protokoll</TableCell>
              <TableCell>Port(s)</TableCell>
              <TableCell>Grund</TableCell>
              <TableCell>Aktiv</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">Keine Risikoports angelegt.</Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((row) => (
              <TableRow key={row.id} sx={{ opacity: row.aktiv ? 1 : 0.6 }}>
                <TableCell>
                  <Chip size="small" variant="outlined" label={PROTOKOLL_LABEL[row.protokoll] ?? row.protokoll.toUpperCase()} />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{portText(row)}</TableCell>
                <TableCell>{row.grund}</TableCell>
                <TableCell>{row.aktiv ? 'Ja' : 'Nein'}</TableCell>
                <TableCell align="right">
                  <IconButton aria-label="bearbeiten" onClick={() => openEdit(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton aria-label="löschen" onClick={() => setDeleteId(row.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Risikoport bearbeiten' : 'Neuer Risikoport'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Protokoll"
            value={form.protokoll}
            onChange={(event) => setForm((prev) => ({ ...prev, protokoll: event.target.value as FormState['protokoll'] }))}
            fullWidth
            margin="normal"
          >
            <MenuItem value="tcp">TCP</MenuItem>
            <MenuItem value="udp">UDP</MenuItem>
            <MenuItem value="any">TCP/UDP</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Port von"
              type="number"
              value={form.port_von}
              onChange={(event) => setForm((prev) => ({ ...prev, port_von: event.target.value }))}
              fullWidth
              margin="normal"
              inputProps={{ min: 0, max: 65535 }}
              error={form.port_von.trim().length > 0 && portVon == null}
            />
            <TextField
              label="Port bis (optional)"
              type="number"
              value={form.port_bis}
              onChange={(event) => setForm((prev) => ({ ...prev, port_bis: event.target.value }))}
              fullWidth
              margin="normal"
              inputProps={{ min: 0, max: 65535 }}
              error={form.port_bis.trim().length > 0 && (portBis == null || (portVon != null && portBis < portVon))}
            />
          </Box>
          <TextField
            label="Grund"
            value={form.grund}
            onChange={(event) => setForm((prev) => ({ ...prev, grund: event.target.value }))}
            fullWidth
            margin="normal"
            placeholder="z. B. RDP aus dem Internet"
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={form.aktiv}
                onChange={(event) => setForm((prev) => ({ ...prev, aktiv: event.target.checked }))}
              />
            )}
            label="Aktiv"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={() => { void save(); }} disabled={!formValid}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteId != null}
        title="Risikoport löschen"
        message="Der Port wird in Firewall-Regeln nicht mehr als Risiko markiert."
        onConfirm={() => { void remove(); }}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
};

export default FirewallRisikoportsPanel;
